import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Collapse,
  Empty,
  Form,
  Input,
  Modal,
  Pagination,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { getSubanta, listSubantasPaginated, updateSubanta, type Subanta } from '../api/subantaApi';
import { listLanguages, type Language } from '../api/languageApi';
import { listRups, RUP_TRIPLE, RUP_VIBHAKTI, updateRup, type Rup, type RupDeclensionKey } from '../api/rupApi';
import { createArtha, isArtha, updateArtha, type Artha } from '../api/arthaApi';
import {
  createNotification,
  listNotifications,
  parseDictionaryNotifications,
  type DictionaryNotification,
} from '../api/notificationApi';
import { ARTHA_BIDHA } from '../constants/arthaEnums';
import { SUBANTA_LINGA } from '../constants/subantaEnums';

function compareArthaDisplayOrder(a: Artha, b: Artha): number {
  const ao = a.order;
  const bo = b.order;
  if (ao != null && bo != null && ao !== bo) return ao - bo;
  if (ao != null && bo == null) return -1;
  if (ao == null && bo != null) return 1;
  return a.id - b.id;
}

function originKey(v: string | null | undefined): string {
  const t = String(v ?? '').trim();
  return t === '' ? '__no_origin__' : t.toLowerCase();
}

function originLabel(v: string | null | undefined): string {
  const t = String(v ?? '').trim();
  return t === '' ? 'No origin' : t;
}

function sameStringArray(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function randomNotificationDefaults(): { email: string; phone: string; comment: string } {
  const email = 'internal@gmail.com';
  const phone = '989898`';
  return { email, phone, comment: '' };
}

type MeaningTableRow = Artha & { __displayOrder: number };

type RupGridProps = {
  rup: Rup;
  editingRupCell: { rupId: number; key: RupDeclensionKey; value: string } | null;
  onStartEditRupCell: (rupId: number, key: RupDeclensionKey, value: string) => void;
  onEditRupCellValue: (value: string) => void;
  onSaveRupCell: () => void;
};

function RupGrid({ rup, editingRupCell, onStartEditRupCell, onEditRupCellValue, onSaveRupCell }: RupGridProps) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', minWidth: 520, borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            {RUP_TRIPLE.map((t) => (
              <th
                key={t}
                style={{
                  border: '1px solid #f0f0f0',
                  padding: 0,
                  background: '#fafafa',
                  textAlign: 'left',
                }}
              >
                {t}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {RUP_VIBHAKTI.map((v) => (
            <tr key={v}>
              {RUP_TRIPLE.map((t) => {
                const k = `${v}_${t}` as RupDeclensionKey;
                const val = rup[k];
                const display = val != null && String(val).trim() !== '' ? String(val) : '—';
                const isEditing = editingRupCell?.rupId === rup.id && editingRupCell?.key === k;
                return (
                  <td
                    key={k}
                    style={{ border: '1px solid #f0f0f0', padding: 0, wordBreak: 'break-word', cursor: 'text' }}
                    onDoubleClick={() => onStartEditRupCell(rup.id, k, display === '—' ? '' : display)}
                  >
                    {isEditing ? (
                      <Input
                        autoFocus
                        size="small"
                        value={editingRupCell.value}
                        onChange={(e) => onEditRupCellValue(e.target.value)}
                        onPressEnter={() => onSaveRupCell()}
                        onBlur={() => onSaveRupCell()}
                      />
                    ) : (
                      display
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SubantaViewPage() {
  const [notificationForm] = Form.useForm<{ email: string; phone: string; comment: string }>();
  const [subantaEditForm] = Form.useForm<{
    word: string;
    base_word: string;
    iast: string;
    bat?: string | null;
    linga: (typeof SUBANTA_LINGA)[number];
    shatwa?: boolean;
    natwa?: boolean;
  }>();
  const [rows, setRows] = useState<Subanta[]>([]);
  const [notificationsBySubanta, setNotificationsBySubanta] = useState<Map<number, DictionaryNotification[]>>(new Map());
  const [languages, setLanguages] = useState<Language[]>([]);
  const [rupsBySubanta, setRupsBySubanta] = useState<Map<number, Rup[]>>(new Map());
  const [loading, setLoading] = useState(false);
  const [showMeanings, setShowMeanings] = useState(true);
  const [showRupawali, setShowRupawali] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [expandAllEnabled, setExpandAllEnabled] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [editingArtha, setEditingArtha] = useState<{ id: number; value: string } | null>(null);
  const [editingArthaOrder, setEditingArthaOrder] = useState<{ id: number; value: string } | null>(null);
  const [editingRupCell, setEditingRupCell] = useState<{ rupId: number; key: RupDeclensionKey; value: string } | null>(
    null
  );
  const [addingMeaningSubantaId, setAddingMeaningSubantaId] = useState<number | null>(null);
  const [activeMeaningTabBySubanta, setActiveMeaningTabBySubanta] = useState<Record<number, number | undefined>>({});
  const [globalMeaningLanguageId, setGlobalMeaningLanguageId] = useState<number | undefined>(undefined);
  const [newMeaningBySubanta, setNewMeaningBySubanta] = useState<
    Record<number, { languageId?: number; text: string; origin: string; order: string }>
  >({});
  const [savingMeaningSubantaId, setSavingMeaningSubantaId] = useState<number | null>(null);
  const [notificationForSubantaId, setNotificationForSubantaId] = useState<number | null>(null);
  const [notificationSaving, setNotificationSaving] = useState(false);
  const [editingSubanta, setEditingSubanta] = useState<Subanta | null>(null);
  const [subantaSaving, setSubantaSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const [langs, allRups, allNotifications] = await Promise.all([listLanguages(), listRups(), listNotifications()]);
        const nextRupMap = new Map<number, Rup[]>();
        for (const r of allRups) {
          const bucket = nextRupMap.get(r.subantaId) ?? [];
          bucket.push(r);
          nextRupMap.set(r.subantaId, bucket);
        }
        const nextNotificationMap = new Map<number, DictionaryNotification[]>();
        for (const n of allNotifications) {
          if (typeof n.subantaId !== 'number') continue;
          const bucket = nextNotificationMap.get(n.subantaId) ?? [];
          bucket.push(n);
          nextNotificationMap.set(n.subantaId, bucket);
        }
        if (!cancelled) {
          setLanguages(langs);
          setRupsBySubanta(nextRupMap);
          setNotificationsBySubanta(nextNotificationMap);
        }
      } catch (err: unknown) {
        const ax = err as { response?: { data?: { message?: string } }; message?: string };
        message.error(ax?.response?.data?.message || ax?.message || 'Failed to load subanta helpers');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const paginated = await listSubantasPaginated(page, limit);
        const details = await Promise.all(paginated.data.map((s) => getSubanta(s.id).catch(() => s)));
        if (!cancelled) {
          setRows(details);
          setTotal(paginated.total);
        }
      } catch (err: unknown) {
        const ax = err as { response?: { data?: { message?: string } }; message?: string };
        message.error(ax?.response?.data?.message || ax?.message || 'Failed to load subanta view');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [page, limit]);

  const languageLabelById = useMemo(() => {
    const map = new Map<number, string>();
    for (const l of languages) map.set(l.id, `${l.name} (${l.code})`);
    return map;
  }, [languages]);

  const meaningColumns = useMemo<ColumnsType<MeaningTableRow>>(
    () => [
      {
        title: 'Order',
        key: 'ord',
        width: 88,
        render: (_: unknown, row: MeaningTableRow) => {
          const isEditing = editingArthaOrder?.id === row.id;
          if (isEditing) {
            return (
              <Input
                autoFocus
                size="small"
                value={editingArthaOrder.value}
                onChange={(e) => setEditingArthaOrder({ id: row.id, value: e.target.value })}
                onPressEnter={() => {
                  void (async () => {
                    const raw = editingArthaOrder.value.trim();
                    const nextOrder = raw === '' ? null : Number(raw);
                    if (raw !== '' && !Number.isFinite(nextOrder)) {
                      message.warning('Order must be a number');
                      return;
                    }
                    try {
                      const updated = await updateArtha(row.id, { order: nextOrder });
                      setRows((prev) =>
                        prev.map((s) => ({
                          ...s,
                          arthas: Array.isArray(s.arthas)
                            ? s.arthas.map((a) => (isArtha(a) && a.id === row.id ? { ...a, ...updated } : a))
                            : s.arthas,
                        }))
                      );
                    } catch (err: unknown) {
                      const ax = err as { response?: { data?: { message?: string } }; message?: string };
                      message.error(ax?.response?.data?.message || ax?.message || 'Failed to update artha order');
                    } finally {
                      setEditingArthaOrder(null);
                    }
                  })();
                }}
                onBlur={() => setEditingArthaOrder(null)}
              />
            );
          }
          return (
            <Typography.Text
              style={{ cursor: 'text' }}
              onDoubleClick={() => setEditingArthaOrder({ id: row.id, value: String(row.order ?? '') })}
            >
              {row.__displayOrder}
            </Typography.Text>
          );
        },
      },
      {
        title: 'Text',
        dataIndex: 'text',
        render: (_: string, row: Artha) => {
          const isEditing = editingArtha?.id === row.id;
          if (isEditing) {
            return (
              <Input.TextArea
                autoFocus
                value={editingArtha.value}
                autoSize={{ minRows: 1, maxRows: 4 }}
                onChange={(e) => setEditingArtha({ id: row.id, value: e.target.value })}
                onPressEnter={(e) => {
                  if (!e.shiftKey) {
                    e.preventDefault();
                    void (async () => {
                      const nextText = editingArtha.value.trim();
                      if (!nextText) {
                        message.warning('Artha text cannot be empty');
                        return;
                      }
                      try {
                        const updated = await updateArtha(row.id, { text: nextText });
                        setRows((prev) =>
                          prev.map((s) => ({
                            ...s,
                            arthas: Array.isArray(s.arthas)
                              ? s.arthas.map((a) => (isArtha(a) && a.id === row.id ? { ...a, ...updated } : a))
                              : s.arthas,
                          }))
                        );
                      } catch (err: unknown) {
                        const ax = err as { response?: { data?: { message?: string } }; message?: string };
                        message.error(ax?.response?.data?.message || ax?.message || 'Failed to update artha');
                      } finally {
                        setEditingArtha(null);
                      }
                    })();
                  }
                }}
                onBlur={() => {
                  void (async () => {
                    const nextText = editingArtha.value.trim();
                    if (!nextText) {
                      setEditingArtha(null);
                      return;
                    }
                    try {
                      const updated = await updateArtha(row.id, { text: nextText });
                      setRows((prev) =>
                        prev.map((s) => ({
                          ...s,
                          arthas: Array.isArray(s.arthas)
                            ? s.arthas.map((a) => (isArtha(a) && a.id === row.id ? { ...a, ...updated } : a))
                            : s.arthas,
                        }))
                      );
                    } catch (err: unknown) {
                      const ax = err as { response?: { data?: { message?: string } }; message?: string };
                      message.error(ax?.response?.data?.message || ax?.message || 'Failed to update artha');
                    } finally {
                      setEditingArtha(null);
                    }
                  })();
                }}
              />
            );
          }
          return (
            <Typography.Paragraph
              style={{ marginBottom: 0, cursor: 'text' }}
              ellipsis={{ rows: 3, expandable: true }}
              onDoubleClick={() => setEditingArtha({ id: row.id, value: row.text ?? '' })}
            >
              {row.text}
            </Typography.Paragraph>
          );
        },
      },
    ],
    [editingArtha, editingArthaOrder]
  );

  const saveRupCell = async () => {
    if (!editingRupCell) return;
    const { rupId, key, value } = editingRupCell;
    const nextValue = value.trim();
    try {
      const updated = await updateRup(rupId, { [key]: nextValue === '' ? null : nextValue });
      setRupsBySubanta((prev) => {
        const next = new Map(prev);
        let found = false;
        next.forEach((list: Rup[], subantaId: number) => {
          if (found) return;
          const idx = list.findIndex((r: Rup) => r.id === rupId);
          if (idx >= 0) {
            const replaced = [...list];
            replaced[idx] = { ...replaced[idx], ...updated };
            next.set(subantaId, replaced);
            found = true;
          }
        });
        return next;
      });
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Failed to update rup');
    } finally {
      setEditingRupCell(null);
    }
  };

  const saveNewMeaning = async (subantaId: number) => {
    const draft = newMeaningBySubanta[subantaId];
    const languageId = draft?.languageId;
    const text = draft?.text?.trim() ?? '';
    if (!languageId) {
      message.warning('Please select a language');
      return;
    }
    if (!text) {
      message.warning('Meaning text is required');
      return;
    }
    setSavingMeaningSubantaId(subantaId);
    try {
      const created = await createArtha({
        subantaId,
        languageId,
        text,
        bidha: ARTHA_BIDHA[0],
        prakar: 'samanya',
      });
      const origin = draft?.origin?.trim() ?? '';
      const orderText = draft?.order?.trim() ?? '';
      let finalArtha = created;
      if (origin || orderText) {
        const parsedOrder = orderText === '' ? null : Number(orderText);
        finalArtha = await updateArtha(created.id, {
          ...(origin ? { origin } : {}),
          ...(orderText ? { order: Number.isFinite(parsedOrder) ? parsedOrder : null } : {}),
        });
      }
      setRows((prev) =>
        prev.map((row) =>
          row.id === subantaId
            ? {
                ...row,
                arthas: [...(Array.isArray(row.arthas) ? row.arthas : []), finalArtha],
              }
            : row
        )
      );
      setNewMeaningBySubanta((prev) => ({ ...prev, [subantaId]: { languageId, text: '', origin: '', order: '' } }));
      setAddingMeaningSubantaId(null);
      message.success('Meaning added');
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Failed to add meaning');
    } finally {
      setSavingMeaningSubantaId(null);
    }
  };

  const submitNotification = async () => {
    if (notificationForSubantaId == null) return;
    try {
      const values = await notificationForm.validateFields();
      setNotificationSaving(true);
      const created = await createNotification({
        subantaId: notificationForSubantaId,
        email: values.email.trim(),
        phone: values.phone.trim(),
        comment: values.comment.trim(),
        resolved: false,
      });
      setRows((prev) =>
        prev.map((row) =>
          row.id === notificationForSubantaId
            ? {
                ...row,
                notifications: [...(Array.isArray(row.notifications) ? row.notifications : []), created],
              }
            : row
        )
      );
      if (typeof created.subantaId === 'number') {
        const subantaId = created.subantaId;
        setNotificationsBySubanta((prev) => {
          const next = new Map(prev);
          const bucket = next.get(subantaId) ?? [];
          next.set(subantaId, [...bucket, created]);
          return next;
        });
      }
      setNotificationForSubantaId(null);
      notificationForm.resetFields();
      message.success('Notification added');
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      if (!String(ax?.message ?? '').toLowerCase().includes('validation')) {
        message.error(ax?.response?.data?.message || ax?.message || 'Failed to add notification');
      }
    } finally {
      setNotificationSaving(false);
    }
  };

  const openEditSubanta = (s: Subanta) => {
    setEditingSubanta(s);
    subantaEditForm.setFieldsValue({
      word: s.word,
      base_word: s.base_word,
      iast: s.iast,
      bat: s.bat ?? '',
      linga: s.linga,
      shatwa: Boolean(s.shatwa),
      natwa: Boolean(s.natwa),
    });
  };

  const submitEditSubanta = async () => {
    if (!editingSubanta) return;
    try {
      const values = await subantaEditForm.validateFields();
      setSubantaSaving(true);
      const updated = await updateSubanta(editingSubanta.id, {
        word: values.word.trim(),
        base_word: values.base_word.trim(),
        iast: values.iast.trim(),
        bat: values.bat == null || String(values.bat).trim() === '' ? null : String(values.bat).trim(),
        linga: values.linga,
        shatwa: Boolean(values.shatwa),
        natwa: Boolean(values.natwa),
      });
      setRows((prev) => prev.map((r) => (r.id === editingSubanta.id ? { ...r, ...updated } : r)));
      setEditingSubanta(null);
      message.success('Subanta updated');
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      if (!String(ax?.message ?? '').toLowerCase().includes('validation')) {
        message.error(ax?.response?.data?.message || ax?.message || 'Failed to update subanta');
      }
    } finally {
      setSubantaSaving(false);
    }
  };

  const items = rows.map((s) => {
    const arthas = Array.isArray(s.arthas) ? s.arthas.filter(isArtha) : [];
    const embeddedNotifications = parseDictionaryNotifications(
      Array.isArray(s.notifications) ? s.notifications : undefined
    );
    const notifications = embeddedNotifications.length > 0 ? embeddedNotifications : notificationsBySubanta.get(s.id) ?? [];
    const langIds = Array.from(new Set(arthas.map((a) => a.languageId))).sort((a, b) => a - b);
    const rups = [...(rupsBySubanta.get(s.id) ?? [])].sort((a, b) => a.id - b.id);
    return {
      key: String(s.id),
      styles: {
        header: { background: 'transparent', border: 'none', padding: 0 },
        body: { padding: '0 8px' },
      },
      extra: (
        <Space size={8}>
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setNotificationForSubantaId(s.id);
              notificationForm.setFieldsValue(randomNotificationDefaults());
            }}
          >
            +
          </Button>
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              openEditSubanta(s);
            }}
          >
            Edit
          </Button>
        </Space>
      ),
      label: (
        <Space
          size={8}
          wrap
          style={{
            fontSize: 16,
            width: '100%',
            paddingTop: 6,
            paddingBottom: 6,
            borderTop: '1px solid #b70606',
          }}
        >
          <Typography.Text strong style={{ fontSize: 18, color: '#b70606' }}>
            {s.word || `Subanta #${s.id}`}
          </Typography.Text>
          <Typography.Text style={{ fontSize: 15, color: '#6f4e37' }}>
            #{s.id}
          </Typography.Text>
          {s.base_word ? (
            <Typography.Text style={{ fontSize: 15, color: '#6f4e37' }}>
              Base: {s.base_word}
            </Typography.Text>
          ) : null}
          <Typography.Text style={{ fontSize: 15, color: '#6f4e37' }}>
            Linga: {s.linga || '—'}
          </Typography.Text>
          <Typography.Text style={{ fontSize: 15, color: '#6f4e37' }}>
            Shatwa: {s.shatwa == null ? '—' : s.shatwa ? 'Yes' : 'No'}
          </Typography.Text>
          <Typography.Text style={{ fontSize: 15, color: '#6f4e37' }}>
            Natwa: {s.natwa == null ? '—' : s.natwa ? 'Yes' : 'No'}
          </Typography.Text>
        </Space>
      ),
      children: (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {showNotifications ? (
            <div>
              {notifications.length > 0 ? (
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  {notifications.map((n) => (
                    <Card key={n.id} size="small" style={{ width: '100%' }} styles={{ body: { padding: 0 } }}>
                      <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        <Typography.Text>
                          <Typography.Text strong>Email:</Typography.Text> {n.email || '—'}
                        </Typography.Text>
                        <Typography.Text>
                          <Typography.Text strong>Phone:</Typography.Text> {n.phone || '—'}
                        </Typography.Text>
                        <Typography.Text>
                          <Typography.Text strong>Comment:</Typography.Text> {n.comment || '—'}
                        </Typography.Text>
                        <Typography.Text type={n.resolved ? 'success' : 'warning'}>
                          {n.resolved ? 'Resolved' : 'Pending'}
                        </Typography.Text>
                      </Space>
                    </Card>
                  ))}
                </Space>
              ) : (
                <></>
              )}
            </div>
          ) : null}

              {showMeanings ? (
                <div>
                  {langIds.length > 0 ? (
                    <Tabs
                      style={{ marginTop: 0 }}
                      tabBarStyle={{ marginBottom: 0 }}
                      size="small"
                      activeKey={String(
                        (globalMeaningLanguageId != null && langIds.includes(globalMeaningLanguageId)
                          ? globalMeaningLanguageId
                          : activeMeaningTabBySubanta[s.id] != null && langIds.includes(activeMeaningTabBySubanta[s.id] as number)
                            ? activeMeaningTabBySubanta[s.id]
                            : langIds[0]) ?? langIds[0]
                      )}
                      onChange={(key) =>
                        setActiveMeaningTabBySubanta((prev) => ({ ...prev, [s.id]: Number(key) }))
                      }
                      items={langIds.map((lid) => {
                        const rowsForLang = arthas.filter((a) => a.languageId === lid);
                        const byOrigin = new Map<string, Artha[]>();
                        const labelByOrigin = new Map<string, string>();
                        for (const a of rowsForLang) {
                          const key = originKey(a.origin);
                          const label = originLabel(a.origin);
                          const bucket = byOrigin.get(key) ?? [];
                          bucket.push(a);
                          byOrigin.set(key, bucket);
                          if (!labelByOrigin.has(key)) labelByOrigin.set(key, label);
                        }
                        const originKeys = Array.from(byOrigin.keys()).sort((x, y) => {
                          if (x === '__no_origin__') return 1;
                          if (y === '__no_origin__') return -1;
                          return x.localeCompare(y, undefined, { sensitivity: 'base', numeric: true });
                        });
                        return {
                          key: String(lid),
                          label: `${languageLabelById.get(lid) ?? `Language #${lid}`} · ${rowsForLang.length}`,
                          children: (
                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                              {originKeys.map((ok) => (
                                <div key={ok}>
                                  <Typography.Text strong>
                                    {labelByOrigin.get(ok) ?? 'No origin'} ({(byOrigin.get(ok) ?? []).length})
                                  </Typography.Text>
                                  <Table<MeaningTableRow>
                                    size="small"
                                    style={{ marginTop: 0 }}
                                    rowKey="id"
                                    columns={meaningColumns}
                                    showHeader={false}
                                    dataSource={[...(byOrigin.get(ok) ?? [])]
                                      .sort(compareArthaDisplayOrder)
                                      .map((a, index) => ({
                                        ...a,
                                        __displayOrder: a.order == null ? index : a.order,
                                      }))}
                                    pagination={false}
                                    scroll={{ x: 680 }}
                                  />
                                </div>
                              ))}
                              <div>
                                <Button
                                  size="small"
                                  onClick={() => {
                                    setAddingMeaningSubantaId((prev) => (prev === s.id ? null : s.id));
                                    setNewMeaningBySubanta((prev) => ({
                                      ...prev,
                                      [s.id]: prev[s.id] ?? { languageId: lid, text: '', origin: '', order: '' },
                                    }));
                                  }}
                                >
                                  +
                                </Button>
                                {addingMeaningSubantaId === s.id ? (
                                  <Space direction="vertical" style={{ width: '100%', marginTop: 8 }} size="small">
                                    <Space wrap style={{ width: '100%' }}>
                                      <Input
                                        placeholder="Meaning text"
                                        style={{ minWidth: 300 }}
                                        value={newMeaningBySubanta[s.id]?.text ?? ''}
                                        onChange={(e) =>
                                          setNewMeaningBySubanta((prev) => ({
                                            ...prev,
                                            [s.id]: {
                                              ...(prev[s.id] ?? {}),
                                              languageId: activeMeaningTabBySubanta[s.id] ?? lid,
                                              text: e.target.value,
                                              origin: prev[s.id]?.origin ?? '',
                                              order: prev[s.id]?.order ?? '',
                                            },
                                          }))
                                        }
                                      />
                                      <Input
                                        placeholder="Origin (optional)"
                                        style={{ width: 180 }}
                                        value={newMeaningBySubanta[s.id]?.origin ?? ''}
                                        onChange={(e) =>
                                          setNewMeaningBySubanta((prev) => ({
                                            ...prev,
                                            [s.id]: {
                                              ...(prev[s.id] ?? {}),
                                              languageId: activeMeaningTabBySubanta[s.id] ?? lid,
                                              text: prev[s.id]?.text ?? '',
                                              origin: e.target.value,
                                              order: prev[s.id]?.order ?? '',
                                            },
                                          }))
                                        }
                                      />
                                      <Input
                                        placeholder="Order (optional)"
                                        style={{ width: 140 }}
                                        value={newMeaningBySubanta[s.id]?.order ?? ''}
                                        onChange={(e) =>
                                          setNewMeaningBySubanta((prev) => ({
                                            ...prev,
                                            [s.id]: {
                                              ...(prev[s.id] ?? {}),
                                              languageId: activeMeaningTabBySubanta[s.id] ?? lid,
                                              text: prev[s.id]?.text ?? '',
                                              origin: prev[s.id]?.origin ?? '',
                                              order: e.target.value,
                                            },
                                          }))
                                        }
                                      />
                                    </Space>
                                    <Space wrap>
                                      <Select
                                        allowClear
                                        placeholder="Select language"
                                        style={{ minWidth: 220 }}
                                        value={newMeaningBySubanta[s.id]?.languageId}
                                        onChange={(nextLanguageId) =>
                                          setNewMeaningBySubanta((prev) => ({
                                            ...prev,
                                            [s.id]: {
                                              ...(prev[s.id] ?? {}),
                                              languageId: nextLanguageId,
                                              text: prev[s.id]?.text ?? '',
                                              origin: prev[s.id]?.origin ?? '',
                                              order: prev[s.id]?.order ?? '',
                                            },
                                          }))
                                        }
                                        options={languages.map((lang) => ({
                                          value: lang.id,
                                          label: `${lang.name} (${lang.code})`,
                                        }))}
                                      />
                                      <Button
                                        type="primary"
                                        loading={savingMeaningSubantaId === s.id}
                                        onClick={() => {
                                          void saveNewMeaning(s.id);
                                        }}
                                      >
                                        Save meaning
                                      </Button>
                                    </Space>
                                  </Space>
                                ) : null}
                              </div>
                            </Space>
                          ),
                        };
                      })}
                    />
                  ) : (
                    <Typography.Paragraph type="secondary" style={{ marginTop: 8 }}>
                      none
                    </Typography.Paragraph>
                  )}
                </div>
              ) : null}

              {showRupawali ? (
                <div>
                  {rups.length > 0 ? (
                    <Space direction="vertical" size="middle" style={{ width: '100%', marginTop: 8 }}>
                      {rups.map((r) => (
                        <div key={r.id}>
                          <Typography.Text>
                            {r.linga}
                          </Typography.Text>
                          <div style={{ marginTop: 6 }}>
                            <RupGrid
                              rup={r}
                              editingRupCell={editingRupCell}
                              onStartEditRupCell={(rupId, key, value) => setEditingRupCell({ rupId, key, value })}
                              onEditRupCellValue={(value) =>
                                setEditingRupCell((prev) => (prev ? { ...prev, value } : prev))
                              }
                              onSaveRupCell={() => {
                                void saveRupCell();
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </Space>
                  ) : (
                    <></>
                  )}
                </div>
              ) : null}
        </Space>
      ),
    };
  });
  const allItemKeys = useMemo(() => items.map((item) => String(item.key)), [items]);
  const allExpanded = allItemKeys.length > 0 && expandedKeys.length === allItemKeys.length;

  useEffect(() => {
    if (expandAllEnabled) {
      setExpandedKeys((prev) => (sameStringArray(prev, allItemKeys) ? prev : allItemKeys));
      return;
    }
    setExpandedKeys((prev) => {
      const filtered = prev.filter((k) => allItemKeys.includes(k));
      return sameStringArray(prev, filtered) ? prev : filtered;
    });
  }, [allItemKeys, expandAllEnabled]);

  return (
    <Card title="Subanta view">
      <Space wrap style={{ marginBottom: 12 }}>
        <Typography.Text strong>Show meanings</Typography.Text>
        <Switch checked={showMeanings} onChange={setShowMeanings} />
        <Typography.Text strong>Meaning language</Typography.Text>
        <Select
          allowClear
          placeholder="All tabs default"
          style={{ minWidth: 220 }}
          value={globalMeaningLanguageId}
          onChange={(value) => setGlobalMeaningLanguageId(value)}
          options={languages.map((lang) => ({
            value: lang.id,
            label: `${lang.name} (${lang.code})`,
          }))}
        />
        <Typography.Text strong>Show rupawali</Typography.Text>
        <Switch checked={showRupawali} onChange={setShowRupawali} />
        <Typography.Text strong>Show notification</Typography.Text>
        <Switch checked={showNotifications} onChange={setShowNotifications} />
        <Button
          onClick={() => {
            if (expandAllEnabled || allExpanded) {
              setExpandAllEnabled(false);
              setExpandedKeys([]);
              return;
            }
            setExpandAllEnabled(true);
            setExpandedKeys(allItemKeys);
          }}
        >
          {allExpanded ? 'Collapse all' : 'Expand all'}
        </Button>
      </Space>

      {loading && items.length === 0 ? (
        <Spin />
      ) : items.length > 0 ? (
        <Spin spinning={loading} tip="Loading subantas...">
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Collapse
              bordered={false}
              ghost
              items={items}
              activeKey={expandedKeys}
              onChange={(keys) => {
                const normalized = Array.isArray(keys) ? keys.map(String) : [String(keys)];
                setExpandedKeys(normalized);
                setExpandAllEnabled(normalized.length > 0 && normalized.length === allItemKeys.length);
              }}
            />
            <Pagination
              current={page}
              pageSize={limit}
              total={total}
              disabled={loading}
              showSizeChanger
              showQuickJumper
              showTotal={(count, range) => `${range[0]}-${range[1]} of ${count}`}
              pageSizeOptions={['10', '20', '50', '100']}
              onChange={(nextPage, nextPageSize) => {
                if (nextPageSize !== limit) {
                  setLimit(nextPageSize);
                  setPage(1);
                  return;
                }
                setPage(nextPage);
              }}
            />
          </Space>
        </Spin>
      ) : (
        <Empty description="No subantas found" />
      )}

      <Modal
        title="Add notification"
        open={notificationForSubantaId != null}
        onCancel={() => {
          setNotificationForSubantaId(null);
          notificationForm.resetFields();
        }}
        onOk={() => void submitNotification()}
        okText="Save"
        confirmLoading={notificationSaving}
      >
        <Form form={notificationForm} layout="vertical">
          <Form.Item name="email" label="Email" rules={[{ required: true, message: 'Email is required' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="phone" label="Phone" rules={[{ required: true, message: 'Phone is required' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="comment" label="Comment" rules={[{ required: true, message: 'Comment is required' }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingSubanta ? `Edit subanta #${editingSubanta.id}` : 'Edit subanta'}
        open={editingSubanta != null}
        onCancel={() => setEditingSubanta(null)}
        onOk={() => void submitEditSubanta()}
        okText="Save"
        confirmLoading={subantaSaving}
      >
        <Form form={subantaEditForm} layout="vertical">
          <Form.Item name="word" label="Word" rules={[{ required: true, message: 'Word is required' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="base_word" label="Base word" rules={[{ required: true, message: 'Base word is required' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="iast" label="IAST" rules={[{ required: true, message: 'IAST is required' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="bat" label="Bat">
            <Input />
          </Form.Item>
          <Form.Item name="linga" label="Linga" rules={[{ required: true, message: 'Linga is required' }]}>
            <Select
              options={SUBANTA_LINGA.map((v) => ({
                value: v,
                label: v,
              }))}
            />
          </Form.Item>
          <Form.Item name="shatwa" label="Shatwa" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="natwa" label="Natwa" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
